import React, { useState, useMemo } from "react";
import {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  PaginationState,
} from "@tanstack/react-table";
import {
  InternationalCallingPlan,
  internationalCallingPlans,
} from "./plan-table-data";
import { getInternationalCallingPlanColumns } from "./plan-columns";
import { Pagination } from "app/components/parts/pagination";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "app/components/ui/table";

interface Props {
  searchValue: string;
}

export const InternationalCallingPlanTable = ({ searchValue }: Props) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 15,
  });

  const columns = getInternationalCallingPlanColumns();

  const filteredData = useMemo(() => {
    if (!internationalCallingPlans) return [];

    if (!searchValue) return internationalCallingPlans;

    return internationalCallingPlans.filter((plan) =>
      plan.destination.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [searchValue]);

  const table = useReactTable<InternationalCallingPlan>({
    data: filteredData,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
    },
  });

  return (
    <React.Fragment>
      <div className="min-w-full max-w-[70vw] overflow-x-auto [&::-webkit-scrollbar]:hidden">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                className="bg-[#FE4D010A] hover:bg-[#FE4D010D]"
              >
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={`border-[#E9EAEB] ${
                    row.index % 2 === 0
                      ? "hover:bg-[#FAFAFA80]"
                      : "bg-[#F8F8F8] hover:bg-[#F8F8F880]"
                  }`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-96 text-center text-black text-[0.775rem] sm:text-[0.825rem] lg:text-sm font-normal leading-4 sm:leading-5"
                >
                  {searchValue
                    ? `No destinations found matching "${searchValue}"`
                    : "No Available Destinations"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {filteredData.length > 0 && (
        <div className="min-w-full max-w-[70vw] px-3 sm:px-4 lg:px-6 py-2 sm:py-3 lg:py-[0.87rem] border-t border-[#E9EAEB]">
          <Pagination table={table} pagination={pagination} />
        </div>
      )}
    </React.Fragment>
  );
};
